import { Box, Button, Card, Checkbox, Col, Form, FormGroup, Heading, Link, Row, TextInput } from '@ksyos/design-system';
import { pick } from 'lodash';
import { useState } from 'react';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';
import type { VektisOrganizationSettings } from '../../utils/vektisUtils';
import {
    getVektisOrganization,
    isVektisOrganizationInactive,
    mapVektisResponseToOrganization,
} from '../../utils/vektisUtils';

type AutoFillCardProps = {
    onAutoFill: (autoFillOrganization: Partial<VektisOrganizationSettings>) => void;
};

export function AutoFillCard({ onAutoFill }: Readonly<AutoFillCardProps>) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    const [agbCode, setAgbCode] = useState<string | null>();
    const [autoFillOrganization, setAutoFillOrganization] = useState<
        Partial<VektisOrganizationSettings> | undefined | null
    >();
    const [searchError, setSearchError] = useState<string | undefined>();
    const [selectedFields, setSelectedFields] = useState<(keyof VektisOrganizationSettings)[]>([]);

    return (
        <Card>
            <Card.Header title={translate.organization.autoFill.searchVektis} />
            <Card.Body>
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={4}
                >
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSearch();
                        }}
                        role="search"
                        submitOnEnter={true}
                    >
                        <FormGroup
                            label={translate.organization.autoFill.searchAgb}
                            layout="vertical"
                        >
                            <Row columnGap={1}>
                                <Col span={8}>
                                    <TextInput
                                        error={searchError}
                                        maxLength={8}
                                        minLength={8}
                                        onChange={async (e) =>
                                            await handleAgbCodeChange(
                                                e.target.value === '' ? undefined : e.target.value
                                            )
                                        }
                                        placeholder={`${translate.organization.agbCode}...`}
                                        value={agbCode ?? ''}
                                    />
                                </Col>
                                <Col span={3}>
                                    <Button type="submit">{translate.common.search}</Button>
                                </Col>
                            </Row>
                        </FormGroup>
                    </Form>

                    {renderOrganizationDetails()}
                </Box>
            </Card.Body>
        </Card>
    );

    function renderCheckBox(label: string, key: keyof VektisOrganizationSettings) {
        if (!autoFillOrganization?.[key]) {
            return null;
        }

        const value =
            key === 'agbCodes' ? (autoFillOrganization.agbCodes?.[0].agbCode ?? '') : autoFillOrganization[key];

        return (
            <>
                <Checkbox
                    checked={selectedFields.includes(key)}
                    key={key}
                    label={label}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedFields([...selectedFields, key]);
                        } else {
                            setSelectedFields(selectedFields.filter((field) => field !== key));
                        }
                    }}
                />
                <div>{value}</div>
            </>
        );
    }

    function renderOrganizationDetails() {
        if (!autoFillOrganization) {
            return null;
        }

        return (
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    onAutoFill(pick(autoFillOrganization, selectedFields));
                }}
                submitOnEnter={true}
            >
                <FormGroup layout="vertical">
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={4}
                    >
                        <Heading size="large">{translate.organization.autoFill.searchResult}</Heading>
                        <Link
                            onClick={toggleAllFields}
                            variant="button"
                        >
                            {translate.organization.autoFill.selectAll}
                        </Link>
                        <Heading size="large">{translate.organization.generalInformation}</Heading>
                        <Box
                            alignItems={'center'}
                            display={'grid'}
                            gap={2}
                            gridTemplateColumns={'1fr 1fr'}
                        >
                            {renderCheckBox(translate.organization.name, 'name')}
                            {renderCheckBox(translate.organization.nameStatutory, 'nameStatutory')}
                            {renderCheckBox(translate.organization.agbCode, 'agbCodes')}
                            {renderCheckBox(translate.organization.kvkNumber, 'kvkNumber')}
                        </Box>

                        <Heading size="large">{translate.organization.contactDetails}</Heading>
                        <Box
                            alignItems={'center'}
                            display={'grid'}
                            gap={2}
                            gridTemplateColumns={'1fr 1fr'}
                        >
                            {renderCheckBox(translate.common.phoneNumber, 'phoneNumber')}
                            {renderCheckBox(translate.common.emailAddress, 'emailAddressRequestInbox')}
                            {renderCheckBox(translate.common.website, 'url')}
                        </Box>
                        <Heading size="large">{translate.organization.address}</Heading>
                        <Box
                            alignItems={'center'}
                            display={'grid'}
                            gap={2}
                            gridTemplateColumns={'1fr 1fr'}
                        >
                            {renderCheckBox(translate.common.street, 'street')}
                            {renderCheckBox(translate.common.houseNumber, 'houseNumber')}
                            {renderCheckBox(translate.common.houseNumberSuffix, 'houseNumberSuffix')}
                            {renderCheckBox(translate.common.zipcode, 'zip')}
                            {renderCheckBox(translate.common.city, 'city')}
                            {renderCheckBox(translate.common.state, 'state')}
                        </Box>
                        <Button type="submit">{translate.organization.autoFill.copySelection}</Button>
                    </Box>
                </FormGroup>
            </Form>
        );
    }

    async function handleSearch() {
        if (agbCode && !/^\d+$/.test(agbCode)) {
            return setSearchError(translate.organization.autoFill.agbOnlyNumbersError);
        }

        if (agbCode) {
            setAutoFillOrganization(null);
            setSearchError(undefined);

            const response = await getVektisOrganization(agbCode, abortableFetch);
            if (!response) {
                return setSearchError(translate.organization.autoFill.noOrganizationFoundError);
            }
            if (response?.status !== 200) {
                return handleErrorCode(response?.status);
            }

            if (isVektisOrganizationInactive(response?.json?.organization)) {
                return setSearchError(translate.organization.autoFill.inactiveOrganizationError);
            }
            const vektisOrganization = mapVektisResponseToOrganization(response.json);
            setAutoFillOrganization(vektisOrganization);
        }
    }

    async function handleAgbCodeChange(value: string | undefined) {
        setSearchError(undefined);
        setAgbCode(value);
    }

    function handleErrorCode(statusCode: number) {
        if (statusCode === 404) {
            return setSearchError(translate.organization.autoFill.noOrganizationFoundError);
        }
        if (statusCode === 400) {
            return setSearchError(translate.organization.autoFill.invalidAgbError);
        }
    }

    function getAutoFillProperties(): string[] {
        if (!autoFillOrganization) {
            return [];
        }

        return Object.keys(autoFillOrganization).filter(
            (key) => !!autoFillOrganization[key as keyof VektisOrganizationSettings]
        );
    }

    function toggleAllFields() {
        const allFields = getAutoFillProperties() as (keyof VektisOrganizationSettings)[];
        if (selectedFields.length === allFields.length) {
            setSelectedFields([]);
        } else {
            setSelectedFields(allFields);
        }
    }
}
